import { useRef, useState } from 'react'

import { IconChevronDown, IconChevronUp } from '@avantstay/backoffice-vectors'

import { BaseCheckPicker } from '../../base'
import { useElementWidth, useListedOptions } from './CheckPicker.hooks'
import * as S from './CheckPicker.styles'
import { CheckPickerProps, ICheckPickerSizeProps } from './CheckPicker.types'
import { MenuHeader } from './components/MenuHeader'
import { OptionCheckbox } from './components/OptionCheckbox'
import { SelectedTags } from './components/SelectedTags'
import { SelectionMenu } from './components/SelectionMenu'

const CheckPicker = <OptionType extends string>({
  actions = true,
  className,
  customAllText,
  customAllValue,
  defaultOpen,
  defaultValue,
  disabled = false,
  groupBy,
  hideAllOption,
  isLoadingOptions,
  isWithinScrollableElement = false,
  maxTagWidth,
  tagLimit,
  onChange,
  onClean,
  onClose,
  onOpen,
  onSearch,
  onSelect,
  options,
  placeholder = 'Select options',
  placement,
  preventOverflow = false,
  searchable = false,
  searchBy,
  shouldLimitTagVisibility,
  small,
  subtitle,
  title,
  value,
}: CheckPickerProps<OptionType> & ICheckPickerSizeProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const toggleRef = useRef<HTMLDivElement>(null)
  const [isOpen, setIsOpen] = useState(false)
  const toggleWidth = useElementWidth(toggleRef)
  const { selectedOptions, unselectedOptions } = useListedOptions({ options, value, isOpen })
  const hasSelection = Boolean(value && value.length > 0)

  return (
    <S.Container
      ref={containerRef}
      className={className}
      $small={small}
      $active={isOpen}
      $disabled={disabled}
      onClick={() => {
        if (!disabled) setIsOpen(true)
      }}
    >
      <BaseCheckPicker
        block
        groupBy={Boolean(groupBy)}
        toggleRef={toggleRef}
        container={() => (isWithinScrollableElement ? containerRef.current : null)}
        cleanable={false}
        defaultOpen={defaultOpen}
        defaultValue={defaultValue}
        disabled={disabled}
        data={unselectedOptions}
        menuClassName="av-picker-menu"
        placeholder={placeholder}
        placement={placement}
        searchable={searchable}
        keepSearchFocused
        value={value}
        onChange={onChange}
        onClose={onClose}
        onClean={onClean}
        onEnter={() => setIsOpen(true)}
        onExit={() => setIsOpen(false)}
        onOpen={onOpen}
        onSearch={onSearch}
        onSelect={onSelect}
        searchBy={searchBy}
        menuStyle={S.MenuStyle}
        preventOverflow={preventOverflow}
        renderMenuGroup={menuGroup => <S.GroupMenu>{menuGroup}</S.GroupMenu>}
        renderMenu={(menu, search) => (
          <SelectionMenu
            menu={menu}
            isLoading={isLoadingOptions}
            searchable={searchable}
            header={
              actions ? (
                <MenuHeader
                  hideAllOption={hideAllOption}
                  customAllText={customAllText}
                  customAllValue={customAllValue}
                  subtitle={subtitle}
                  hasSelection={hasSelection}
                  selectedOptions={selectedOptions}
                  value={value}
                  onClean={onClean}
                  onSelect={onSelect}
                />
              ) : null
            }
            {...search}
          />
        )}
        renderMenuItem={(label, option) => (
          <OptionCheckbox label={label} option={option} value={value} onSelect={onSelect} />
        )}
        renderValue={(_selectedValues, _selectedItems, selectedElement) => (
          <SelectedTags
            options={options}
            value={value}
            title={title}
            width={toggleWidth}
            maxTagWidth={maxTagWidth}
            tagLimit={tagLimit}
            shouldLimitTagVisibility={shouldLimitTagVisibility}
            selectedElement={selectedElement}
          />
        )}
      />
      {hasSelection ? null : (
        <S.DropdownIndicator $disabled={disabled}>
          {isOpen ? <IconChevronUp /> : <IconChevronDown />}
        </S.DropdownIndicator>
      )}
    </S.Container>
  )
}

const CheckPickerDefault = <OptionType extends string>(props: CheckPickerProps<OptionType>) => (
  <CheckPicker {...props} />
)

const CheckPickerSmall = <OptionType extends string>(props: CheckPickerProps<OptionType>) => (
  <CheckPicker {...props} small />
)

CheckPicker.Default = CheckPickerDefault
CheckPicker.Small = CheckPickerSmall

export { CheckPicker }
